import _                     from 'underscore';
import { reactLocalStorage } from 'reactjs-localstorage';

import { EnvironmentObject }                             from '../environments/types/base';
import { STRING_CONVERSION }                             from '../constants/FormatStringEnums';
import { defaultCorvalPreferences, PreferenceStateType } from './usePrefStates';

//@ts-ignore
let environment: EnvironmentObject = __ENV__;

const suffixDelimiter = STRING_CONVERSION.DELIMITER;

type UsePrefStateResolverHookMethod = () => UsePrefStateResolverHook;

type PreferenceStateMigrationRecord = {

    // when this migration set should become effective
    effectiveTS: number,

    // dictionary of readable keys we want to alter / reset
    alterations?: Partial<PreferenceStateType>

    // hashID that was used before its removal, reference: usePrefStates.tsx -> defaultCorvalPreferences
    removals?: string[]
}

export type UsePrefStateResolverHook = {
    resolvePreferenceUpdates(): {
        outdated: object,
        updates: Partial<PreferenceStateType>,
        removals: string[],
        resolved: number | null,
        success: boolean,
    };
};

const now = () => {

    return new Date().getTime();
}

// Retrieves storage key name from environment
function getPrefsStorageKeyName(): string{

    return environment.session.cookies.prefs.namedKey
}

function getPrefsResolverKeyName(): string{

    return environment.session.cookies.prefResolver.namedKey
}

// Retrives raw value
function getCookieValue<D>( key: string, asObject = true ): D{

    return asObject
           ? reactLocalStorage.getObject( key )
           : reactLocalStorage.get( key );
}

// Retrives raw object value or an empty object
function getPrefsStorageValue(){

    return getCookieValue<object>( getPrefsStorageKeyName() );
}

// Saves some hash value
function setPrefsStorageValue( value ){

    reactLocalStorage.setObject( getPrefsStorageKeyName(), value );
}

// Retrives raw object value or an empty object
function getPrefResolverStorageValue(){

    const value = getCookieValue<string>( getPrefsResolverKeyName(), false );

    return value ? parseInt( value ) : value;
}

// Saves some hash value
function setPrefResolverValue(){

    const resolvedTS = now();

    reactLocalStorage.setObject( getPrefsResolverKeyName(), resolvedTS );

    return resolvedTS;
}

function getDefaultPrefs(){

    return _.mapObject( defaultCorvalPreferences, ( value ) => value.default )
}

function resolvePrefValues( migrations: PreferenceStateMigrationRecord[] ){

    const lastResolveTS = getPrefResolverStorageValue();

    let outdatedPrefs                                       = {},
        appliedMigrations: PreferenceStateMigrationRecord[] = [],
        appliedRemovals                                     = [],
        updatedPrefs                                        = getPrefsStorageValue(),
        resolvedTS;

    try{

        migrations.map( ( { alterations: _alterations, removals = [], effectiveTS = now() } ) => {

            if( !lastResolveTS || ( effectiveTS > lastResolveTS ) ){

                // store hash to value mapping
                const valueMapping: Map<string, any> = new Map( [] );

                // ensure alterations exist to operate on. If empty, use default for everything.
                let alterations = ( !_alterations || !_.isObject( _alterations ) )
                                  ? getDefaultPrefs()
                                  : _alterations;

                // go over the alterations, getting hash IDs
                _.mapObject( alterations, ( value, key ) => {

                    const { hashId } = defaultCorvalPreferences[ key ];

                    valueMapping.set( hashId, value );

                } )

                // Evaluate prefs, altering them if changes are needed
                updatedPrefs = _.mapObject( updatedPrefs, ( value, key ) => {

                    const [ prefhashId, ...cryptoKeyPart ] = key.split( suffixDelimiter );

                    if( valueMapping.has( prefhashId ) ){

                        outdatedPrefs[ key ] = value;

                        return valueMapping.get( prefhashId );
                    }

                    return value;
                } );

                // remove any keys specified by the prefhashId
                if( removals.length ){

                    _.keys( updatedPrefs ).map( key => {

                        const [ prefhashId, ...cryptoKeyPart ] = key.split( suffixDelimiter );

                        if( removals.includes( prefhashId ) ){

                            appliedRemovals.push( prefhashId );

                            delete updatedPrefs[ key ];
                        }
                    } )
                }

                // Record what was applied
                appliedMigrations.push( { alterations, effectiveTS } );
            }

        } );

        if( appliedMigrations.length ){

            // Set the correct values
            setPrefsStorageValue( updatedPrefs );

            // Timestamp the change
            resolvedTS = setPrefResolverValue();
        }

        return {
            outdated: outdatedPrefs,
            updates:  appliedMigrations,
            removals: appliedRemovals,
            resolved: resolvedTS,
            success:  true,
        }

    } catch( e ) {

        return {
            outdated: {},
            updates:  migrations,
            removals: [],
            resolved: null,
            success:  false,
        }

    }
}

export const usePrefStateResolver: UsePrefStateResolverHookMethod = () => {

    // Return an update function to run once, applying updates to each pref key in storage
    const resolvePreferenceUpdates = _.once( _.partial( resolvePrefValues, migrations ) ) as UsePrefStateResolverHook['resolvePreferenceUpdates'];

    return { resolvePreferenceUpdates };
};

// Migration pattern, applies changes linearly like a DB migration would.
// When do the following alterations need to effectively occur?
// What specific settings need to be set / updated
const migrations: PreferenceStateMigrationRecord[] = [

    // Prepares for budget data set inclusion reconfig
    {
        effectiveTS: Date.parse( "2023-03-08" ),
        alterations: {
            BUDGET_VISUALIZATION_BUDGET_ITEMS_VISIBLE:                            true,
            BUDGET_VISUALIZATION_BUDGET_PROFESSIONAL_FEE_ITEMS_VISIBLE:           true,
            BUDGET_VISUALIZATION_BUDGET_OPERATIONAL_COST_ITEMS_VISIBLE:           true,
            BUDGET_VISUALIZATION_BUDGET_ONGOING_COST_ITEMS_VISIBLE:               true,
            BUDGET_VISUALIZATION_BUDGET_RECURRING_PROFESSIONAL_FEE_ITEMS_VISIBLE: true,
            BUDGET_VISUALIZATION_BUDGET_RECURRING_OPERATIONAL_COST_ITEMS_VISIBLE: true,
        },
        removals:    [
            'P51', // BUDGET_VISUALIZATION_BUDGET_RECURRENCE_ITEMS_VISIBLE
        ]
    },

    // Prepares for additional filtering keys on objective roadmap map
    {
        effectiveTS: Date.parse( "2023-03-30" ),
        alterations: {
            MAP_ACTIVE_FILTERS:                null,
            OBJECTIVES_ROADMAP_ACTIVE_FILTERS: null,
        }
    },

    // Prepares for additional filtering keys on budget visualization
    {
        effectiveTS: Date.parse( "2023-08-24" ),
        alterations: {
            BUDGET_VISUALIZATION_BY_HIRE_ACTIVE_FILTERS:     null,
            BUDGET_VISUALIZATION_BY_CATEGORY_ACTIVE_FILTERS: null,
        },
        removals:    [
            'P62', // BUDGET_VISUALIZATION_BY_CATEGORY_TIMEFRAME_DATES
        ]
    },

    // Prepares for additional filtering keys on budget visualization
    {
        effectiveTS: Date.parse( "2023-09-26" ),
        alterations: {
            OBJECTIVES_ROADMAP_TIMEFRAME_DATES:       [ null, null ],
            OBJECTIVES_ROADMAP_YEARS_IN_QUARTER_VIEW: [],
        },
        removals:    []
    },

    // Prepares for additional filtering keys on budget visualization
    {
        effectiveTS: Date.parse( "2024-01-31" ),
        alterations: {
            TOOLS_ACTIVITY_SEARCH_TABLE_STATE: null,
        },
        removals:    []
    },

    // Prepares for additional global settings for project visibility
    {
        effectiveTS: Date.parse( "2024-04-23" ),
        alterations: {
            GLOBAL_ACTIVE_PROJECT_VISIBILITY: [],
        },
        removals:    []
    },

    // Prepares for additional global settings for project versions
    {
        effectiveTS: Date.parse( "2024-09-10" ),
        alterations: {
            PROJECT_VERSION_ID: null,
        },
        removals:    []
    },

];
